import { createFileRoute, Link } from "@tanstack/react-router";
import { ClockIcon } from "lucide-react";
import { Image } from "@/components/image";
import { FAQ } from "@/components/www/sections/angebote/faq";
import { Hero } from "@/components/www/sections/angebote/hero";
import { SchoolBooking } from "@/components/www/sections/angebote/school-booking";
import {
  ScaffoldSection,
  ScaffoldSectionContainer,
  ScaffoldSectionDescription,
  ScaffoldSectionHeader,
  ScaffoldSectionTitle,
} from "@/components/www/sections/scaffold";
import { ACTIVITIES__NEW__ } from "@/lib/constants";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/_www/angebote/bowling/")({
  component: RouteComponent,
  head: () =>
    seo({
      title: "Bowling – Alte Papierfabrik Wuppertal",
      description:
        "Bowling in der Alten Papierfabrik Wuppertal – 12 Hightech-Bahnen und eine riesige LED-Wand. Öffnungszeiten, Preise und online buchen.",
      path: "/angebote/bowling",
      image: "/images/og/bowling.jpg",
      imageAlt: "Bowlingkugel, Pins und Bowlingschuhe auf einer Bowlingbahn",
    }),
});

const hours = [
  {
    days: "Montag - Donnerstag",
    open: "12:00 - 23:00",
  },
  {
    days: "Freitag & Samstag",
    open: "12:00 - 01:00",
  },
  {
    days: "Sonntag",
    open: "11:00 - 22:00",
  },
  {
    days: "Vorfeiertage",
    open: "12:00 - 01:00",
  },
  // {
  //   days: "Feiertage",
  //   open: "10:00 - 22:00",
  // },
];

const prices = [
  {
    day: "Montag - Donnerstag",
    times: [
      { time: "12:00 - 18:00", price: 20.0 },
      { time: "18:00 - 23:00", price: 25.0 },
    ],
  },
  {
    day: "Freitag & Samstag",
    times: [
      { time: "12:00 - 18:00", price: 25.0 },
      { time: "18:00 - 01:00", price: 35.0 },
    ],
  },
  {
    day: "Sonntag",
    times: [{ time: "11:00 - 22:00", price: 33.0 }],
  },
  {
    day: "Vorfeiertage",
    times: [
      { time: "12:00 - 18:00", price: 25.0 },
      { time: "18:00 - 01:00", price: 35.0 },
    ],
  },
];

const currencyFormatter = new Intl.NumberFormat("de", {
  style: "currency",
  currency: "EUR",
});

const FAMILY_FRIENDS_PRICE = 33.0;
const FLATRATE_PRICE = 12.0;

function RouteComponent() {
  return (
    <>
      <Hero
        backgroundVideo={{
          src: "/videos/bowling.mp4",
          alt: "Bowling",
          poster: "/images/bowling_1.png",
        }}
        bookingButton={{
          label: "Jetzt buchen",
          href: "#buchen",
          target: "_self",
          backgroundColor: ACTIVITIES__NEW__.bowling.brand.primaryColor,
          foregroundColor:
            ACTIVITIES__NEW__.bowling.brand.primaryForegroundColor,
        }}
        description="12 Hightech-Bahnen und eine riesige LED-Wand – für dein perfektes Bowlingerlebnis"
        highlights={ACTIVITIES__NEW__.bowling.highlights}
        logo={ACTIVITIES__NEW__.bowling.brand.logo}
        primaryColor={ACTIVITIES__NEW__.bowling.brand.primaryColor}
        title="Next Level Bowling"
      />
      <SchoolBooking activity="bowling" />
      <ScaffoldSection className="py-0 md:gap-4 md:py-4">
        <ScaffoldSectionContainer className="md:grid lg:grid-cols-2">
          <div className="pb-12">
            <ScaffoldSectionHeader className="mb-6">
              <ScaffoldSectionTitle
                style={{
                  textBoxTrim: "trim-end",
                }}
              >
                Öffnungszeiten
              </ScaffoldSectionTitle>
            </ScaffoldSectionHeader>
            <div
              className="overflow-hidden rounded-2xl shadow-xl"
              style={{
                backgroundColor: ACTIVITIES__NEW__.bowling.brand.primaryColor,
                color: ACTIVITIES__NEW__.bowling.brand.primaryForegroundColor,
              }}
            >
              {/* Hours List */}
              <div className="space-y-3 p-3">
                {hours.map((schedule, index) => (
                  <div
                    className="rounded-xl bg-background p-4 text-white transition-all hover:scale-[1.02] sm:p-5"
                    key={index}
                    style={{}}
                  >
                    <div className="flex flex-col gap-3">
                      {/* Days */}
                      <div className="min-w-[140px] font-medium text-lg leading-trim sm:text-xl">
                        {schedule.days}
                      </div>

                      {/* Times Container */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6 lg:flex-col lg:items-start lg:gap-3">
                        {/* Opening Hours */}
                        <div className="flex items-center gap-2">
                          <ClockIcon className="size-5 opacity-70" />
                          <span className="font-normal text-base leading-trim">
                            {schedule.open} Uhr
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer Note */}
              <div
                className="border-t px-3 py-4 text-sm sm:px-8"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderColor: "rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className="flex items-start gap-2">
                  <svg
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <title>Information</title>
                    <path
                      clipRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      fillRule="evenodd"
                    />
                  </svg>
                  <div className="opacity-75">
                    <strong>Küchenzeiten:</strong> Di - Fr ab 15:00 Uhr, Sa & So
                    ab 14:00 Uhr
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pb-12">
            <ScaffoldSectionHeader className="mb-6">
              <ScaffoldSectionTitle>Preise</ScaffoldSectionTitle>
            </ScaffoldSectionHeader>
            <div className="overflow-hidden rounded-2xl bg-neutral-600 shadow-xl">
              {/* Price List */}
              <div className="space-y-3 p-3">
                {prices.map((pricing, index) => (
                  <div
                    className="rounded-xl p-4 transition-all hover:scale-[1.02] sm:p-5"
                    key={index}
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <div className="flex flex-col gap-3">
                      {/* Days */}
                      <div className="min-w-[140px] font-medium text-lg leading-trim sm:text-xl">
                        {pricing.day}
                      </div>

                      {/* Price Times Container */}
                      <div className="flex flex-col gap-2">
                        {pricing.times.map((timeSlot, timeIndex) => (
                          <div
                            className="flex w-full items-center justify-between gap-2"
                            key={timeIndex}
                          >
                            <span className="text-sm leading-trim">
                              {timeSlot.time} Uhr
                            </span>
                            <span className="font-semibold text-sm leading-trim">
                              {currencyFormatter.format(timeSlot.price)} / Bahn
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer Note */}
              <div
                className="border-t px-3 py-4 text-sm sm:px-8"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderColor: "rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <svg
                      className="mt-0.5 h-4 w-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <title>Information</title>
                      <path
                        clipRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        fillRule="evenodd"
                      />
                    </svg>
                    <div className="opacity-75">
                      <div>Preise pro Stunde / Bahn</div>
                      <div className="mt-1">
                        Bowling-Leihschuhe einmalig:{" "}
                        {currencyFormatter.format(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pb-12 lg:col-span-2">
            <ScaffoldSectionHeader className="mb-6">
              <ScaffoldSectionTitle>Aktionen</ScaffoldSectionTitle>
            </ScaffoldSectionHeader>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="relative aspect-[711/400]">
                <Image
                  alt="Frame"
                  className="absolute inset-0 object-cover"
                  height={400}
                  src="/images/frame.png"
                  width={711}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center text-background">
                  <h3 className="font-bold text-3xl">Happy Hour</h3>
                  <p className="text-lg">
                    Jumbos nur {currencyFormatter.format(9.9)}
                    <br />
                    Caipis nur {currencyFormatter.format(7.9)}
                  </p>
                  <p className="font-light text-xs">Jeden Tag ab 20:00 Uhr</p>
                </div>
              </div>

              <div className="relative aspect-[711/400]">
                <Image
                  alt="Frame"
                  className="absolute inset-0 object-cover"
                  height={400}
                  src="/images/frame.png"
                  width={711}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-background">
                  <h3 className="font-bold text-3xl">Family & Friends*</h3>
                  <p className="text-lg">
                    Nur {currencyFormatter.format(FAMILY_FRIENDS_PRICE)} /
                    Stunde
                  </p>
                  <p className="font-light text-xs">
                    Sonntag 11:00 - 16:00 Uhr
                    <br />
                    Inkl. Leihschuhe + Gratis Kindercocktail (bis 14 Jahre)
                    <br />
                    Nicht mit anderen Aktionen kombinierbar.
                  </p>
                </div>
              </div>

              <div className="relative aspect-[711/400]">
                <Image
                  alt="Frame"
                  className="absolute inset-0 object-cover"
                  height={400}
                  src="/images/frame.png"
                  width={711}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-background">
                  <h3 className="font-bold text-3xl">Flatrate Bowling*</h3>
                  <p className="text-lg">
                    Nur {currencyFormatter.format(FLATRATE_PRICE)} / Person
                  </p>
                  <p className="font-light text-xs">
                    Nur Sonntags
                    <br />
                    3 Std. Spielzeit inkl. Leihschuhe
                    <br />
                    Slot 1: 16:00 - 19:00 Uhr
                    <br />
                    Slot 2: 19:00 - 22:00 Uhr
                    <br />
                    Nur die angegebenen Slots buchbar.
                    <br />
                    Ab 9 Pers. auf zwei Bahnen spielbar
                    <br />
                    (bei weniger wird für 9 berechnet).
                  </p>
                </div>
              </div>

            </div>
            <p className="mt-6 text-center text-sm opacity-75">
              *Aktionen haben an Feiertagen sowie an Vorfeiertagen keine
              Gültigkeit
            </p>
          </div>

          <div className="pb-12 md:pb-0 lg:col-span-2">
            <ScaffoldSectionHeader className="mb-6">
              <ScaffoldSectionTitle>
                Häufig gestellte Fragen
              </ScaffoldSectionTitle>
            </ScaffoldSectionHeader>
            <FAQ
              backgroundColor={ACTIVITIES__NEW__.bowling.brand.primaryColor}
              foregroundColor={
                ACTIVITIES__NEW__.bowling.brand.primaryForegroundColor
              }
              questions={[
                {
                  question: "Buchung der Bahnen",
                  answer: (
                    <div>
                      <p className="mb-2">
                        <strong>
                          Pro Bahn max. 10 Personen, buchbar telefonisch unter
                          0202/317 99 95 oder online!
                        </strong>{" "}
                        Im Buchungskalender sehen Sie sofort, ob Ihr
                        Wunschtermin verfügbar ist. Die Bahnnummer ist nicht
                        verbindlich! Nach erfolgreicher Online-Buchung, erhalten
                        Sie von uns eine Buchungsbestätigung per E-Mail mit
                        Ihrer Buchungsnummer, bitte diese bei Ihrem Besuch
                        vorlegen.
                      </p>
                      <p>
                        Wenn Sie Ihre Buchung stornieren möchten, ist dies 24
                        Stunden vor Spielbeginn kostenfrei möglich, bitte
                        benutzen Sie hierfür den Stornierungslink in Ihrer
                        Bestätigungsemail. Für spätere Stornierungen berechnen
                        wir 4 € pro Spieler. Änderungswünsche bitte per Email:{" "}
                        <strong>buchen@altepapierfabrik.eu</strong> mit der
                        entsprechenden Buchungsnummer senden.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Spieldauer",
                  answer: (
                    <div>
                      <p className="mb-2">
                        Ihre Spielzeit (ca.): bei 1 - 5 Personen = 2 Spiele pro
                        Stunde, bei 6 - 8 Personen = 1 Spiel pro Stunde
                      </p>
                      <p className="mb-2">
                        (Das sind Erfahrungswerte und können nach Spielweise
                        variieren)
                      </p>
                      <p>
                        Wir berechnen pro Stunde als Gruppe gesamt. Bitte haben
                        Sie dafür Verständnis, dass Sie nach Ablauf der
                        gebuchten Stunde für die nachfolgende Gruppe Ihr Spiel
                        unterbrechen müssen, auch wenn Sie ein Spiel nicht zu
                        Ende gespielt haben.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Bowling-Leihschuhe",
                  answer: (
                    <div>
                      <p>
                        Aus Versicherungs- und Sicherheitsgründen müssen auf der
                        Bowling-Anlage unsere Bowling-Leihschuhe getragen
                        werden. Die Leihgebühr der Bowlingschuhe beträgt
                        einmalig 2€ pro Person.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Getränke & Speisen",
                  answer: (
                    <div>
                      <p>
                        Die Mitnahme von eigenen Getränken und Speisen ist
                        untersagt. Gerne können Sie in der SPORTS-BAR einen
                        Tisch zum Essen und Trinken reservieren.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Kindergeburtstag",
                  answer: (
                    <div>
                      <p className="mb-3">
                        Kindergeburtstage können von Montag bis Freitag von
                        15:00 bis 18:00 Uhr und Samstag und Sonntag von 12:00
                        bis 18:00 Uhr (ab 5 Kinder von 5 bis 15 Jahre) gefeiert
                        werden.{" "}
                        <strong>
                          Bitte Kindergeburtstage{" "}
                          <a
                            className="text-red-600 underline hover:no-underline"
                            href="#buchen"
                          >
                            online
                          </a>{" "}
                          oder telefonisch unter 0202/317 99 95 buchen.
                        </strong>
                      </p>
                      <p className="mb-3">
                        <strong>
                          Zur Auswahl stehen 2 Kindergeburtstags-Pakete:
                        </strong>
                      </p>
                      <div className="mb-4">
                        <p className="mb-2">
                          <strong>Deal 1 - </strong>beinhaltet für alle
                          Teilnehmer:
                        </p>
                        <ul className="mb-2 list-outside list-disc space-y-1 pl-4">
                          <li>2 Stunden bowlen</li>
                          <li>+ Bowling-Leihschuhe</li>
                          <li>
                            + 1 Kids-Menue Ihrer Wahl
                            <br />
                            Nuggets / Burger / Schnitzel mit Pommes
                          </li>
                          <li>+ 1 Soft-Getränk 0,25 l</li>
                        </ul>
                        <p>
                          <strong>Di. bis Do.: Pro Kind 12,90 €</strong>
                        </p>
                        <p>
                          <strong>Fr. bis So.: Pro Kind 14,90 €</strong>
                        </p>
                      </div>
                      <div className="mb-3">
                        <p className="mb-2">
                          <strong>Deal Deluxe - </strong>beinhaltet für alle
                          Teilnehmer:
                        </p>
                        <ul className="mb-2 list-outside list-disc space-y-1 pl-4">
                          <li>2 Stunden bowlen</li>
                          <li>+ Bowling-Leihschuhe</li>
                          <li>
                            + 1 Kids-Menue Ihrer Wahl
                            <br />
                            Nuggets / Burger / Schnitzel mit Pommes
                          </li>
                          <li>+ 2 Soft-Getränke 0,25 l</li>
                          <li>+ 1 Muffin</li>
                        </ul>
                        <p>
                          <strong>Di. bis Do.: Pro Kind 14,90 €</strong>
                        </p>
                        <p>
                          <strong>Fr. bis So.: Pro Kind 16,90 €</strong>
                        </p>
                      </div>
                      <p>
                        Leistungen, die über dieses Paket hinaus in Anspruch
                        genommen werden, werden extra berechnet. Bitte beachten
                        Sie, dass mitgebrachte Speisen und Getränke nicht
                        erlaubt sind.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Parkmöglichkeiten",
                  answer: (
                    <div>
                      <p>
                        Für unsere Gäste stehen Parkplätze auf unserem Parkplatz
                        direkt vor der Alten Papierfabrik zur Verfügung! Sollten
                        diese alle besetzt sein, dann finden Sie rundum der
                        Alten Papierfabrik genug öffentliche Parkmöglichkeiten.
                      </p>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </ScaffoldSectionContainer>
      </ScaffoldSection>
      <ScaffoldSection id="buchen">
        <ScaffoldSectionContainer>
          <ScaffoldSectionHeader>
            <ScaffoldSectionTitle>Jetzt Buchen</ScaffoldSectionTitle>
            <ScaffoldSectionDescription>
              Buchen Sie bequem online über unseren Buchungskalender! Hier sehen
              Sie sofort die Verfügbarkeit für Ihren Wunschtermin.
            </ScaffoldSectionDescription>
          </ScaffoldSectionHeader>
          <iframe
            frameBorder="0"
            height="900"
            scrolling="yes"
            src="https://4bowl.de/altepapierfabrik/online/?widget=1&sig=uNDvjsBhtf5Afd4E"
            style={{ minWidth: "100%" }}
            title="Bowling buchen"
            width="100%"
          />
        </ScaffoldSectionContainer>
      </ScaffoldSection>
      <ScaffoldSection id="kindergeburtstag">
        <ScaffoldSectionContainer>
          <ScaffoldSectionHeader>
            <ScaffoldSectionTitle>Kindergeburtstag</ScaffoldSectionTitle>
            <ScaffoldSectionDescription>
              Feiert euren Kindergeburtstag bei uns auf der Bowlingbahn!
            </ScaffoldSectionDescription>
          </ScaffoldSectionHeader>
          <div className="mb-8 overflow-hidden rounded-2xl bg-neutral-600 shadow-xl">
            <div className="space-y-4 p-6">
              <div>
                <p className="mb-3 font-medium text-lg">
                  Zur Auswahl stehen 2 Kindergeburtstags-Pakete:
                </p>
                <div className="mb-4 rounded-xl bg-black/10 p-4">
                  <p className="mb-2 font-semibold text-lg">Deal 1</p>
                  <p className="mb-2">Beinhaltet für alle Teilnehmer:</p>
                  <ul className="mb-3 list-outside list-disc space-y-1 pl-4">
                    <li>2 Stunden bowlen</li>
                    <li>Bowling-Leihschuhe</li>
                    <li>
                      1 Kids-Menue nach Wahl (Nuggets / Burger / Schnitzel mit
                      Pommes)
                    </li>
                    <li>1 Soft-Getränk 0,25 l</li>
                  </ul>
                  <p>
                    <strong>Di. bis Do.:</strong> Pro Kind 12,90 €
                  </p>
                  <p>
                    <strong>Fr. bis So.:</strong> Pro Kind 14,90 €
                  </p>
                </div>
                <div className="mb-4 rounded-xl bg-black/10 p-4">
                  <p className="mb-2 font-semibold text-lg">Deal Deluxe</p>
                  <p className="mb-2">Beinhaltet für alle Teilnehmer:</p>
                  <ul className="mb-3 list-outside list-disc space-y-1 pl-4">
                    <li>2 Stunden bowlen</li>
                    <li>Bowling-Leihschuhe</li>
                    <li>
                      1 Kids-Menue nach Wahl (Nuggets / Burger / Schnitzel mit
                      Pommes)
                    </li>
                    <li>2 Soft-Getränke 0,25 l</li>
                    <li>1 Muffin</li>
                  </ul>
                  <p>
                    <strong>Di. bis Do.:</strong> Pro Kind 14,90 €
                  </p>
                  <p>
                    <strong>Fr. bis So.:</strong> Pro Kind 16,90 €
                  </p>
                </div>
              </div>
              <div className="rounded-xl bg-black/10 p-4">
                <p className="mb-2 font-semibold">Wichtige Hinweise:</p>
                <ul className="list-outside list-disc space-y-1 pl-4 text-sm">
                  <li>
                    Mindestens 5 Kinder erforderlich (bei weniger Kindern werden
                    trotzdem 5 berechnet)
                  </li>
                  <li>Eltern zählen nicht als Kind</li>
                  <li>
                    Erst ab 10 Kindern gibt es zwei Bahnen (bei weniger Kindern
                    muss die zweite Bahn normal bezahlt werden)
                  </li>
                  <li>
                    Das Mitbringen von Kuchen oder Torte ist nicht gestattet
                  </li>
                  <li>
                    Montags ist die Küche geschlossen – kein Kindergeburtstag
                    möglich
                  </li>
                </ul>
              </div>
              <p className="text-sm opacity-75">
                Leistungen, die über das Paket hinaus in Anspruch genommen
                werden, werden extra berechnet.
              </p>
            </div>
          </div>
          <iframe
            frameBorder="0"
            height="900"
            scrolling="yes"
            src="https://4bowl.de/altepapierfabrik/online/?widget=1&sig=uNDvjsBhtf5Afd4E&action=eventlink&token=NE5RanZIbEtjL3pGQUxhVTgycmU2dz09"
            style={{ minWidth: "100%" }}
            title="Kindergeburtstag buchen"
            width="100%"
          />
        </ScaffoldSectionContainer>
      </ScaffoldSection>
    </>
  );
}
